import type { LocationQuery } from 'vue-router';
import type { ValidData as AdvancedSearchSearchFormValidData } from '@/components/searchForms/AdvancedSearchSearchForm.vue';
import type { ValidData as BookedAirRoadSearchFormValidData } from '@/components/searchForms/BookedAirRoadSearchForm.vue';
import type { ValidData as DashboardSearchFormValidData } from '@/components/searchForms/DashboardSearchForm.vue';
import type { ValidData as DispatchDriverSearchFormValidData } from '@/components/searchForms/DispatchDriverSearchForm.vue';
import type { ValidData as DispatchJobSearchFormValidData } from '@/components/searchForms/DispatchJobSearchForm.vue';
import type { ValidData as JobsAirRoadSearchFormValidData } from '@/components/searchForms/JobsAirRoadSearchForm.vue';
import type { ValidData as SmsDailySearchFormValidData } from '@/components/searchForms/SmsDailySearchForm.vue';
import type { ValidData as TicketsDashboardSearchFormValidData } from '@/components/searchForms/TicketsDashboardSearchForm.vue';

import { format } from 'date-fns';
import _mapValues from 'lodash/mapValues';
import _pickBy from 'lodash/pickBy';

export function buildAdvancedSearchSearchLocationQuery(
  routeLocationQuery: LocationQuery,
  formData: AdvancedSearchSearchFormValidData,
): LocationQuery {
  const dates = [
    format(formData.dates[0], 'yyyy-MM-dd'),
    format(formData.dates[1], 'yyyy-MM-dd'),
  ].join(',');
  const locationQuery = {
    ...routeLocationQuery,
    dates,
    filters: formData.filters?.join(','),
    states: formData.states?.join(','),
    customers: formData.customerCodes?.join(','),
    services: formData.serviceCodes?.join(','),
    drivers: formData.driverIds?.join(','),
    serial: formData.serialNumber,
    address: formData.stopAddress,
    combined: formData.combinedData,
    pay_on_hold:
      formData.payOnHold != null ? String(formData.payOnHold) : undefined,
  };
  return locationQuery as LocationQuery;
}

export function buildBookedAirRoadSearchLocationQuery(
  routeLocationQuery: LocationQuery,
  formData: BookedAirRoadSearchFormValidData,
): LocationQuery {
  const dates = [
    format(formData.dates[0], 'yyyy-MM-dd'),
    format(formData.dates[1], 'yyyy-MM-dd'),
  ].join(',');
  let locationQuery: object = {
    ...routeLocationQuery,
    dates,
    status: formData.status,
    customer: formData.customer,
    search: formData.search,
    date_type: formData.dateType,
  };
  // remove empty values
  locationQuery = _pickBy(locationQuery, (item: unknown) => {
    return item != null && item !== '';
  });
  return locationQuery as LocationQuery;
}

export function buildDashboardSearchLocationQuery(
  routeLocationQuery: LocationQuery,
  formData: DashboardSearchFormValidData,
): LocationQuery {
  const customObject: object = { ...routeLocationQuery, ...formData };
  // tranform arrays into separated comma strings
  let locationQuery = _mapValues(customObject, (item) => {
    if (Array.isArray(item)) return (item as unknown[]).join(',');
    return item;
  });
  // remove empty values
  locationQuery = _pickBy(locationQuery, (item: unknown) => {
    return item != null && item !== '';
  });
  return locationQuery as LocationQuery;
}

export function buildDispatchDriverSearchLocationQuery(
  routeLocationQuery: LocationQuery,
  formData: DispatchDriverSearchFormValidData,
): LocationQuery {
  let locationQuery: object = {
    ...routeLocationQuery,
    driver_search: formData.search,
    driver_fleets: formData.fleets?.join(','),
    driver_type: formData.driverFilters.type?.join(','),
    driver_zone: formData.driverFilters.zone?.join(','),
  };
  // remove empty values
  locationQuery = _pickBy(locationQuery, (item: unknown) => {
    return item != null && item !== '';
  });
  return locationQuery as LocationQuery;
}

export function buildDispatchJobSearchLocationQuery(
  routeLocationQuery: LocationQuery,
  formData: DispatchJobSearchFormValidData,
): LocationQuery {
  let locationQuery: object = {
    ...routeLocationQuery,
    date: formData.date,
    job_search: formData.search,
    job_service_areas: formData.serviceAreas?.join(','),
    job_service_groups: formData.serviceGroups?.join(','),
    job_sites: formData.moreFilters.sites?.join(','),
    job_goods: formData.moreFilters.goods?.join(','),
    is_job_can_schedule: formData.moreFilters.switches.isCanSchedule,
    is_job_pay_on_hold: formData.moreFilters.switches.isPayOnHold,
    is_job_invoice: formData.moreFilters.switches.isInvoice,
  };
  // remove empty values
  locationQuery = _pickBy(locationQuery, (item: unknown) => {
    return item != null && item !== '';
  });
  return locationQuery as LocationQuery;
}

export function buildJobsAirRoadSearchLocationQuery(
  routeLocationQuery: LocationQuery,
  formData: JobsAirRoadSearchFormValidData,
): LocationQuery {
  const dates = [
    format(formData.dates[0], 'yyyy-MM-dd'),
    format(formData.dates[1], 'yyyy-MM-dd'),
  ].join(',');
  let locationQuery: object = {
    ...routeLocationQuery,
    dates,
    customer: formData.customer,
    states: formData.states?.join(','),
    search: formData.search,
    only_920_driver: formData.isOnly920Driver,
    exclude_920_driver: formData.isExclude920Driver,
    from_warehouse: formData.isFromWarehouse,
    to_warehouse: formData.isToWarehouse,
  };
  // remove empty values
  locationQuery = _pickBy(locationQuery, (item: unknown) => {
    return item != null && item !== '';
  });
  return locationQuery as LocationQuery;
}

export function buildSmsDailyLocationQuery(
  routeLocationQuery: LocationQuery,
  formData: SmsDailySearchFormValidData,
): LocationQuery {
  const locationQuery: LocationQuery = {
    ...routeLocationQuery,
  };

  if (formData.date) {
    locationQuery.date = formData.date;
  } else {
    delete locationQuery.date;
  }

  if (formData.division) {
    locationQuery.division = formData.division;
  } else {
    delete locationQuery.division;
  }

  if (formData.driver) {
    locationQuery.driver = formData.driver.toString();
  } else {
    delete locationQuery.driver;
  }

  return locationQuery;
}

export function buildSmsHistoryLocationQuery(
  routeLocationQuery: LocationQuery,
  formData: { dateRange: [Date, Date] | null; driver: number | null },
): LocationQuery {
  const locationQuery: LocationQuery = {
    ...routeLocationQuery,
  };

  if (formData.dateRange) {
    locationQuery.dates = [
      format(formData.dateRange[0], 'yyyy-MM-dd'),
      format(formData.dateRange[1], 'yyyy-MM-dd'),
    ].join(',');
  } else {
    delete locationQuery.dates;
  }

  if (formData.driver) {
    locationQuery.driver = formData.driver.toString();
  } else {
    delete locationQuery.driver;
  }

  return locationQuery;
}

export function buildTicketsDashboardSearchLocationQuery(
  routeLocationQuery: LocationQuery,
  formData: TicketsDashboardSearchFormValidData,
): LocationQuery {
  let locationQuery: object = {
    ...routeLocationQuery,
    status: formData.status?.join(','),
    search: formData.search,
    is_rfc: formData.isRfc?.toString(),
    is_unread: formData.isUnread?.toString(),
  };
  // remove empty values
  locationQuery = _pickBy(locationQuery, (item: unknown) => {
    return item != null && item !== '';
  });
  return locationQuery as LocationQuery;
}
